import { action, computed, makeObservable, observable } from 'mobx'
import { task } from 'mobx-task'
import { func, validationContext } from 'validx'
import ModalViewModel from 'infra/ModalViewModel'
import { AlertFactory } from 'alerts/Alert'
import { Bundle } from 'admin/bundle/bundle'
import { BundleStore } from 'admin/bundle/BundleStore'
import { RefreshBundles } from 'admin/bundle/BundleSearchViewModel'
import {
  formatBundleNames,
  pluralizeBundles,
  showBulkActionError,
  showBulkActionResult,
} from 'admin/bundle/BulkActionAlerts'
import { Id } from 'types/definitions'

export interface BundleDiscountPreview {
  bundle: Bundle
  currentPrice: number
  newPrice: number
}

export class BulkApplyDiscountViewModel extends ModalViewModel {
  @observable
  selectedBundles: Bundle[] = []

  @observable
  percentOff = 0

  validation = validationContext(this, {
    percentOff: [
      func(
        ({ value }) => value > 0 && value <= 100,
        'Enter a percentage greater than 0 and no more than 100.',
      ),
    ],
  })

  constructor(
    private bundleStore: BundleStore,
    private newAlert: AlertFactory,
    private refreshBundles: RefreshBundles,
  ) {
    super()
    makeObservable(this)

    this.close$.subscribe(this.reset)
  }

  /**
   * Archived bundles can't be discounted, so they never make it into the
   * preview or the request.
   */
  @computed
  get eligibleBundles() {
    return this.selectedBundles.filter((bundle) => !bundle.archived)
  }

  @computed
  get archivedCount() {
    return this.selectedBundles.length - this.eligibleBundles.length
  }

  @computed
  get hasEligibleBundles() {
    return this.eligibleBundles.length > 0
  }

  @computed
  get hasArchivedBundles() {
    return this.archivedCount > 0
  }

  @computed
  get previews(): BundleDiscountPreview[] {
    return this.eligibleBundles.map((bundle) => ({
      bundle,
      currentPrice: bundle.price,
      newPrice: this.newPriceFor(bundle.price),
    }))
  }

  @computed
  get hasDiscount() {
    return this.percentOff > 0
  }

  /**
   * A percentage over 100 drives the price below $0. Those projections block
   * the apply and are called out in red.
   */
  @computed
  get negativePreviews() {
    return this.previews.filter((preview) => preview.newPrice < 0)
  }

  @computed
  get negativePriceWarning() {
    const bundles = this.negativePreviews.map((preview) => preview.bundle)

    if (bundles.length === 0) {
      return undefined
    }

    return `Lower the percentage — ${bundles.length} ${pluralizeBundles(bundles.length)} would fall below $0: ${formatBundleNames(bundles)}.`
  }

  @computed
  get canApply() {
    return (
      this.hasDiscount &&
      this.previews.length > 0 &&
      this.negativePreviews.length === 0
    )
  }

  /**
   * Computed in integer cents so float representation can't shift a price by a
   * cent. The server stores what we send, so this rounding is the only one.
   */
  private newPriceFor(price: number) {
    const priceCents = Math.round(price * 100)
    const cents = Math.round((priceCents * (100 - this.percentOff)) / 100)

    return cents / 100
  }

  @action
  open(bundles: Bundle[]) {
    this.reset()
    this.selectedBundles = bundles

    this.show()
  }

  @action.bound
  reset() {
    this.selectedBundles = []
    this.percentOff = 0

    this.submit.reset()
    this.validation.reset()
  }

  submit = task.resolved(async () => {
    if (!this.validation.reset().validate().isValid || !this.canApply) {
      return
    }

    const previews = this.previews

    try {
      const result = await this.bundleStore.bulkEditPrices(
        Id.assert(previews[0].bundle.workspace.id),
        previews.map((preview) => ({
          bundleId: Id.assert(preview.bundle.Id),
          price: preview.newPrice,
        })),
      )

      const skippedIds = new Set<Id>([
        ...result.notFoundIds,
        ...result.archivedIds,
      ])

      showBulkActionResult(
        this.newAlert,
        previews.map((preview) => preview.bundle),
        skippedIds,
        {
          success: (count) =>
            `Discount applied to ${count} ${pluralizeBundles(count)}.`,
          partialTitle: 'Some bundles were skipped',
          partial: (count, skipped) =>
            `Discounted ${count} of ${count + skipped.length} bundles. Skipped: ${formatBundleNames(skipped)}.`,
        },
      )
    } catch (e) {
      showBulkActionError(
        this.newAlert,
        e,
        'You do not have permission to apply discounts in this workspace.',
        'There was an error applying the discount.',
      )
      this.close()
      return
    }

    await this.refreshBundles(previews[0].bundle.workspace)

    this.close()
  })
}
